import type { CSSProperties, HTMLAttributes } from 'react';
import { cx } from '../internal/cx';
import styles from './Skeleton.module.css';

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'text' | 'rect' | 'circle';
  width?: string | number;
  height?: string | number;
  /** Number of text lines (text variant only). The last line is shorter. */
  lines?: number;
}

/**
 * Loading placeholder. Always aria-hidden: set `aria-busy="true"` on the region being loaded
 * (and provide a status message or Spinner label) so assistive technology knows it is loading.
 */
export function Skeleton({
  variant = 'text',
  width,
  height,
  lines = 1,
  className,
  style,
  ...rest
}: SkeletonProps) {
  const size: CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style,
  };

  if (variant === 'text' && lines > 1) {
    return (
      <span className={cx(styles.stack, className)} aria-hidden="true" {...rest}>
        {Array.from({ length: lines }, (_, i) => (
          <span
            key={i}
            className={cx(styles.skeleton, styles.text)}
            style={{ ...size, width: i === lines - 1 ? '60%' : size.width }}
          />
        ))}
      </span>
    );
  }

  return (
    <span
      className={cx(styles.skeleton, styles[variant], className)}
      style={size}
      aria-hidden="true"
      {...rest}
    />
  );
}
